<template>
    <base-dialog :loading="loading" :value="dialog" :width="460" :fullscreen="isMobile"
        :title="$t('ai.alert_settings_title')" @input="val => { if (!val) hide() }" @close="hide">
        <div class="alert-form">
            <div class="form-group">
                <div class="switch-row">
                    <span class="field-label">{{ $t('ai.alert_settings_active') }}</span>
                    <v-switch v-model="active" color="primary" hide-details dense class="ma-0 pa-0" />
                </div>
                <span class="hint">{{ $t('ai.alert_settings_active_hint') }}</span>
            </div>
            <div class="form-group">
                <span class="field-label">{{ $t('ai.alert_settings_muscle_hurt') }}</span>
                <v-text-field solo flat dense hide-details type="number" step="0.1" min="0" max="5"
                    v-model.number="muscleHurtThreshold" hide-details />
            </div>
            <div class="form-group">
                <span class="field-label">{{ $t('ai.alert_settings_energy') }}</span>
                <v-text-field solo flat dense hide-details type="number" step="0.1" min="0" max="5"
                    v-model.number="energyThreshold" hide-details />
            </div>
            <div class="form-group">
                <span class="field-label">{{ $t('ai.alert_settings_adherence') }}</span>
                <v-text-field solo flat dense hide-details type="number" step="1" min="0" max="100"
                    v-model.number="adherenceThreshold" hide-details />
            </div>
            <div class="form-group">
                <span class="field-label">{{ $t('ai.alert_settings_window') }}</span>
                <v-text-field solo flat dense hide-details type="number" step="1" min="1" max="30"
                    v-model.number="windowDays" hide-details />
            </div>
            <div class="form-group">
                <span class="field-label">{{ $t('ai.alert_settings_cooldown') }}</span>
                <v-text-field solo flat dense hide-details type="number" step="1" min="0" max="30"
                    v-model.number="cooldownDays" hide-details />
                <span class="hint">{{ $t('ai.alert_settings_cooldown_hint') }}</span>
            </div>
        </div>

        <template v-slot:footer>
            <dialog-actions :confirm-label="$t('common.save')" :loading="loading" @cancel="hide" @confirm="save" />
        </template>
    </base-dialog>
</template>

<script>
import { mapState } from 'vuex'
import BaseDialog from '@/components/common/BaseDialog'
import DialogActions from '@/components/common/DialogActions'
import { DEFAULT_ALERT_SETTINGS, loadAlertSettings, saveAlertSettings } from '@/utils/AiAlertSettings'

export default {
    name: 'AiAlertSettingsDialog',
    components: { BaseDialog, DialogActions },

    data() {
        return {
            dialog: false,
            loading: false,
            studentId: null,
            active: DEFAULT_ALERT_SETTINGS.active,
            muscleHurtThreshold: DEFAULT_ALERT_SETTINGS.muscleHurtThreshold,
            energyThreshold: DEFAULT_ALERT_SETTINGS.energyThreshold,
            adherenceThreshold: DEFAULT_ALERT_SETTINGS.adherenceThreshold,
            windowDays: DEFAULT_ALERT_SETTINGS.windowDays,
            cooldownDays: DEFAULT_ALERT_SETTINGS.cooldownDays,
        }
    },

    computed: {
        ...mapState(['currentUser']),
        coachId() {
            return this.currentUser?.uid
        },
    },

    methods: {
        async show(studentId) {
            const vm = this
            vm.studentId = studentId
            vm.dialog = true
            await vm.load()
        },
        hide() {
            this.dialog = false
        },
        async load() {
            const vm = this
            const settings = await loadAlertSettings(vm.coachId, vm.studentId)
            vm.active = settings.active
            vm.muscleHurtThreshold = settings.muscleHurtThreshold
            vm.energyThreshold = settings.energyThreshold
            vm.adherenceThreshold = settings.adherenceThreshold
            vm.windowDays = settings.windowDays
            vm.cooldownDays = settings.cooldownDays
        },
        async save() {
            const vm = this
            vm.loading = true
            try {
                await saveAlertSettings(vm.coachId, vm.studentId, {
                    active: vm.active,
                    muscleHurtThreshold: vm.muscleHurtThreshold,
                    energyThreshold: vm.energyThreshold,
                    adherenceThreshold: vm.adherenceThreshold,
                    windowDays: vm.windowDays,
                    cooldownDays: vm.cooldownDays,
                })
                vm.toast(vm.$t('ai.alert_settings_saved'))
                vm.hide()
            } finally {
                vm.loading = false
            }
        },
    },
}
</script>

<style scoped>
.alert-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.switch-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.field-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.hint {
    font-size: 12px;
    font-weight: 400;
    color: var(--v-txtMuted-base);
}
</style>
